import { motion, AnimatePresence } from "framer-motion";
import { Users, Car, FileCheck, Plane, Monitor } from "lucide-react";
import { useLanguage } from "@/contexts/language-context";
import { useState, useEffect } from "react";

export function OurFeatures() {
  const { t } = useLanguage();
  const [currentImageIndexes, setCurrentImageIndexes] = useState<Record<number, number>>({});

  const features = [
    {
      icon: Monitor,
      title: t("ourFeatures.smartMedia.title"),
      description: t("ourFeatures.smartMedia.description"),
      images: [
        "/img/mp5-.webp",
        "/img/mp5-2-.webp",
      ],
      stats: [
        { label: t("ourFeatures.smartMedia.display"), value: t("ourFeatures.smartMedia.displayValue") },
        { label: t("ourFeatures.smartMedia.functions"), value: t("ourFeatures.smartMedia.functionsValue") },
      ],
      highlights: [
        t("ourFeatures.feature5"),
        t("ourFeatures.feature6"),
      ]
    },
    {
      icon: Users,
      title: t("ourFeatures.comfortableSeating.title"),
      description: t("ourFeatures.comfortableSeating.description"),
      images: [
        "/img/内饰1排-1.webp",
        "/img/内饰2排.webp",
        "/img/内饰3排.webp"
      ],
      stats: [
        { label: t("ourFeatures.comfortableSeating.seats"), value: "7" },
        { label: t("ourFeatures.comfortableSeating.design"), value: t("ourFeatures.comfortableSeating.designValue") },
        { label: t("ourFeatures.comfortableSeating.comfort"), value: "★★★★★" },
      ],
      highlights: [
        t("ourFeatures.feature4"),
        t("ourFeatures.feature2"),
      ]
    },
    {
      icon: Car,
      title: t("ourFeatures.largeSpace.title"),
      description: t("ourFeatures.largeSpace.description"),
      images: [
        "/img/透视图-无尺寸.webp",
        "/img/全景图-恢复的.webp",
      ],
      stats: [
        { label: t("ourFeatures.largeSpace.trunkDepth"), value: "1.2*1.2*1.3" },
        { label: t("ourFeatures.largeSpace.trunkDepth2"), value: "2.0*1.2*1.3" },
        { label: t("ourFeatures.largeSpace.capacity"), value: "6×27\"" },
      ],
      highlights: [
        t("ourFeatures.feature1"),
        t("ourFeatures.feature2"),
      ]
    },
    {
      icon: FileCheck,
      title: t("ourFeatures.privateLicense.title"),
      description: t("ourFeatures.privateLicense.description"),
      images: [
        "/img/11.webp"
      ],
      stats: [
        { label: t("ourFeatures.privateLicense.licenseType"), value: t("ourFeatures.privateLicense.licenseValue") },
        { label: t("ourFeatures.privateLicense.vehicleClass"), value: t("ourFeatures.privateLicense.title").split("-")[0].trim() },
      ],
      highlights: [
        t("ourFeatures.feature3"),
        t("ourFeatures.feature4"),
      ]
    },
    {
      icon: Plane,
      title: t("ourFeatures.rideshare.title"),
      description: t("ourFeatures.rideshare.description"),
      images: [
        "/img/V7七座版-可以在机场充电效果图.jpg"
      ],
      stats: [
        { label: t("ourFeatures.rideshare.airportAccess"), value: "✓" },
        { label: t("ourFeatures.rideshare.passengers"), value: "7" },
      ],
      highlights: [
        t("ourFeatures.feature3"),
      ]
    },
  ];

  useEffect(() => {
    const intervals: NodeJS.Timeout[] = [];
    
    features.forEach((feature, featureIndex) => {
      if (feature.images.length > 1) {
        const interval = setInterval(() => {
          setCurrentImageIndexes((prev) => ({
            ...prev,
            [featureIndex]: ((prev[featureIndex] || 0) + 1) % feature.images.length
          }));
        }, 5000);
        intervals.push(interval);
      }
    });

    return () => {
      intervals.forEach(interval => clearInterval(interval));
    };
  }, [features.length]);

  return (
    <section className="py-24 bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-block mb-4 px-6 py-2 bg-blue-100 rounded-full">
            <span className="text-blue-600 font-semibold">{t("ourFeatures.badge")}</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            {t("ourFeatures.title")}
            <span className="block text-blue-600">{t("ourFeatures.titleHighlight")}</span>
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            {t("ourFeatures.subtitle")}
          </p>
        </motion.div>

        <div className="space-y-24 max-w-7xl mx-auto">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2, duration: 0.8 }}
                className={`grid grid-cols-1 lg:grid-cols-10 gap-12 items-center ${
                  index % 2 === 1 ? "lg:grid-flow-dense" : ""
                }`}
              >
                <div className={`relative lg:col-span-7 ${index % 2 === 1 ? "lg:col-start-4" : ""}`}>
                  <div className="grid gap-4">
                    {feature.images.length === 1 ? (
                      <div className="relative aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl">
                        <img 
                          src={feature.images[0]}
                          alt={feature.title}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                      </div>
                    ) : (
                      <div className="relative aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl">
                        <AnimatePresence mode="wait">
                          <motion.img
                            key={`${index}-${currentImageIndexes[index] || 0}`}
                            src={feature.images[currentImageIndexes[index] || 0]}
                            alt={`${feature.title} ${(currentImageIndexes[index] || 0) + 1}`}
                            className="w-full h-full object-cover"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.8 }}
                          />
                        </AnimatePresence>
                        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                        
                        {/* 轮播指示器 */}
                        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
                          {feature.images.map((_, imgIndex) => (
                            <button
                              key={imgIndex}
                              onClick={() => setCurrentImageIndexes(prev => ({ ...prev, [index]: imgIndex }))}
                              className={`w-2 h-2 rounded-full transition-all ${
                                (currentImageIndexes[index] || 0) === imgIndex 
                                  ? "bg-white w-8" 
                                  : "bg-white/50"
                              }`}
                              aria-label={`Go to image ${imgIndex + 1}`}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-2xl shadow-xl">
                    {feature.stats.map((stat, statIndex) => (
                      <div key={stat.label} className={statIndex < feature.stats.length - 1 ? "mb-3" : ""}>
                        <p className="text-sm text-gray-500">{stat.label}</p>
                        <p className="text-2xl font-bold text-blue-600">{stat.value}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className={`lg:col-span-3 ${index % 2 === 1 ? "lg:col-start-1 lg:row-start-1" : ""}`}>
                  <h3 className="text-3xl md:text-4xl font-bold mb-4 flex items-center gap-3">
                    <Icon className="w-8 h-8 text-blue-600" />
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                    {feature.description}
                  </p>

                  <div className="space-y-4">
                    {feature.highlights.map((highlight) => (
                      <div key={highlight} className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mt-1">
                          <div className="w-2 h-2 bg-blue-600 rounded-full" />
                        </div>
                        <span className="text-gray-700">{highlight}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
